// src/components/admin/InvoiceManagement.js
import { useState, useMemo, useEffect } from 'react';
import { collection, doc, addDoc, updateDoc, deleteDoc, Timestamp, onSnapshot, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import {
  FileText, Search, Loader2, Check, Clock, DollarSign, Mail,
  Plus, Trash2, Edit2, X, Send, Eye, AlertTriangle, ChevronDown, ChevronUp,
  Copy, Building2, Hash, Printer
} from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function generateInvoiceNumber() {
  const now = new Date();
  const y = now.getFullYear().toString().slice(-2);
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `INV-${y}${m}-${rand}`;
}

function formatDateInput(date) {
  return new Date(date).toISOString().split('T')[0];
}

// ─── Status config ───────────────────────────────────────────────────────────
const STATUSES = {
  draft:   { label: 'Draft',   color: 'bg-gray-100 text-gray-700' },
  sent:    { label: 'Sent',    color: 'bg-blue-100 text-blue-700' },
  viewed:  { label: 'Viewed',  color: 'bg-purple-100 text-purple-700' },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700' },
  paid:    { label: 'Paid',    color: 'bg-green-100 text-green-700' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700' },
  void:    { label: 'Void',    color: 'bg-gray-100 text-gray-400' },
};

const TERMS_OPTIONS = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on Receipt'];

// Remit-to address shown on invoices (also duplicated in functions/src/index.ts
// for the emailed version)
const REMIT_TO = {
  name: '4Skin, LLC',
  street: '3330 Sunrise Ave, Suite 110',
  cityStateZip: 'Las Vegas, NV 89101'
};

// ─── PDF Generator ───────────────────────────────────────────────────────────
function openInvoicePDF(invoice) {
  const dueDate = invoice.dueDate?.toDate?.() || new Date(invoice.dueDate);
  const invoiceDate = invoice.invoiceDate?.toDate?.() || new Date(invoice.invoiceDate);
  const isPastDue = new Date() > dueDate && invoice.status !== 'paid';

  const lineItemsHTML = (invoice.lineItems || []).map(item =>
    '<tr>' +
    '<td>' + item.description + (item.sku ? '<br><span style="font-size:11px;color:#999;">SKU: ' + item.sku + '</span>' : '') + '</td>' +
    '<td class="r">' + item.quantity + '</td>' +
    '<td class="r">$' + Number(item.unitPrice).toFixed(2) + '</td>' +
    '<td class="r">$' + (item.quantity * item.unitPrice).toFixed(2) + '</td>' +
    '</tr>'
  ).join('');

  let extraTotals = '';
  if (invoice.discount) extraTotals += '<tr><td>Discount' + (invoice.discountLabel ? ' (' + invoice.discountLabel + ')' : '') + '</td><td class="r">-$' + Number(invoice.discount).toFixed(2) + '</td></tr>';
  if (invoice.tax) extraTotals += '<tr><td>Tax</td><td class="r">$' + Number(invoice.tax).toFixed(2) + '</td></tr>';
  if (invoice.shipping) extraTotals += '<tr><td>Shipping</td><td class="r">$' + Number(invoice.shipping).toFixed(2) + '</td></tr>';

  let balanceHTML = '';
  if ((invoice.amountPaid || 0) > 0) {
    balanceHTML = '<tr><td>Amount Paid</td><td class="r">-$' + Number(invoice.amountPaid).toFixed(2) + '</td></tr>' +
      '<tr class="grand"><td>Balance Due</td><td class="r">$' + ((invoice.total || 0) - (invoice.amountPaid || 0)).toFixed(2) + '</td></tr>';
  }

  const statusClass = invoice.status === 'paid' ? 'status-paid' : isPastDue ? 'status-overdue' : 'status-pending';
  const statusText = invoice.status === 'paid' ? 'PAID' : isPastDue ? 'PAST DUE' : 'PENDING';

  const shipTo = invoice.shippingAddress
    ? '<p>' + (invoice.shippingAddress.name || '') + '</p><p>' + (invoice.shippingAddress.street || '') + '</p><p>' + (invoice.shippingAddress.city || '') + ', ' + (invoice.shippingAddress.state || '') + ' ' + (invoice.shippingAddress.zip || '') + '</p>'
    : '<p>Same as billing</p>';

  const html = '<!DOCTYPE html><html><head><title>Invoice ' + invoice.invoiceNumber + '</title>' +
    '<style>' +
    '* { margin:0; padding:0; box-sizing:border-box; }' +
    'body { font-family:"Helvetica Neue",Arial,sans-serif; padding:40px 50px; color:#1a1a1a; max-width:850px; margin:0 auto; }' +
    '.header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:40px; padding-bottom:20px; border-bottom:3px solid #000; }' +
    '.logo { font-size:28px; font-weight:800; letter-spacing:-0.5px; }' +
    '.logo-sub { font-size:11px; color:#666; margin-top:2px; letter-spacing:1px; text-transform:uppercase; }' +
    '.inv-title { text-align:right; }' +
    '.inv-title h1 { font-size:36px; font-weight:300; letter-spacing:4px; color:#333; }' +
    '.inv-title .inv-num { font-size:14px; color:#666; margin-top:4px; }' +
    '.meta-row { display:flex; justify-content:space-between; margin-bottom:35px; }' +
    '.meta-block h3 { font-size:10px; text-transform:uppercase; letter-spacing:2px; color:#999; margin-bottom:8px; }' +
    '.meta-block p { font-size:13px; line-height:1.6; }' +
    '.meta-block .company { font-weight:700; font-size:14px; }' +
    '.status-badge { display:inline-block; padding:4px 14px; border-radius:3px; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:1px; }' +
    '.status-paid { background:#d1fae5; color:#065f46; }' +
    '.status-pending { background:#fef3c7; color:#92400e; }' +
    '.status-overdue { background:#fee2e2; color:#991b1b; }' +
    'table { width:100%; border-collapse:collapse; margin-bottom:30px; }' +
    'thead th { background:#f8f8f8; padding:12px 16px; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:1.5px; color:#666; border-bottom:2px solid #e5e5e5; }' +
    'thead th.r { text-align:right; }' +
    'tbody td { padding:14px 16px; border-bottom:1px solid #f0f0f0; font-size:13px; }' +
    'tbody td.r { text-align:right; font-variant-numeric:tabular-nums; }' +
    'tbody tr:last-child td { border-bottom:2px solid #e5e5e5; }' +
    '.totals { width:300px; margin-left:auto; margin-bottom:40px; }' +
    '.totals tr td { padding:6px 16px; font-size:13px; }' +
    '.totals tr td.r { text-align:right; font-variant-numeric:tabular-nums; }' +
    '.totals .grand td { font-weight:800; font-size:18px; padding-top:12px; border-top:3px solid #000; }' +
    '.notes { background:#fafafa; padding:20px; border-radius:4px; margin-bottom:30px; }' +
    '.notes h4 { font-size:10px; text-transform:uppercase; letter-spacing:2px; color:#999; margin-bottom:8px; }' +
    '.notes p { font-size:12px; color:#555; line-height:1.6; }' +
    '.footer { margin-top:40px; padding-top:20px; border-top:1px solid #e5e5e5; text-align:center; }' +
    '.footer p { font-size:11px; color:#999; margin-bottom:4px; }' +
    '.footer .pay { font-size:13px; color:#333; font-weight:600; margin-bottom:8px; }' +
    '@media print { body { padding:20px; } .no-print { display:none !important; } }' +
    '</style></head><body>' +
    '<div class="no-print" style="text-align:center;margin-bottom:20px;">' +
    '<button onclick="window.print()" style="padding:10px 24px;background:#000;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:14px;">Print / Save as PDF</button>' +
    '</div>' +
    '<div class="header"><div><div class="logo">4Skin</div><div class="logo-sub">Athletic Tape — Wholesale</div></div>' +
    '<div class="inv-title"><h1>INVOICE</h1><div class="inv-num">' + invoice.invoiceNumber + '</div></div></div>' +
    '<div class="meta-row">' +
    '<div class="meta-block"><h3>Bill To</h3><p class="company">' + (invoice.customerName || '') + '</p><p>' + (invoice.customerContact || '') + '</p><p>' + (invoice.customerEmail || '') + '</p>' + (invoice.customerAddress ? '<p>' + invoice.customerAddress + '</p>' : '') + '</div>' +
    '<div class="meta-block"><h3>Ship To</h3>' + shipTo + '</div>' +
    '<div class="meta-block" style="text-align:right;"><h3>Invoice Details</h3>' +
    '<p><strong>Date:</strong> ' + invoiceDate.toLocaleDateString() + '</p>' +
    '<p><strong>Due:</strong> ' + dueDate.toLocaleDateString() + '</p>' +
    '<p><strong>Terms:</strong> ' + (invoice.terms || 'Net 30') + '</p>' +
    '<p style="margin-top:8px;"><span class="status-badge ' + statusClass + '">' + statusText + '</span></p></div></div>' +
    '<table><thead><tr><th>Description</th><th class="r">Qty</th><th class="r">Unit Price</th><th class="r">Amount</th></tr></thead>' +
    '<tbody>' + lineItemsHTML + '</tbody></table>' +
    '<table class="totals">' +
    '<tr><td>Subtotal</td><td class="r">$' + (invoice.subtotal || 0).toFixed(2) + '</td></tr>' +
    extraTotals +
    '<tr class="grand"><td>Total Due</td><td class="r">$' + (invoice.total || 0).toFixed(2) + '</td></tr>' +
    balanceHTML +
    '</table>' +
    (invoice.notes ? '<div class="notes"><h4>Notes</h4><p>' + invoice.notes + '</p></div>' : '') +
    '<div class="footer">' +
    '<p style="font-size:10px;color:#999;text-transform:uppercase;letter-spacing:2px;margin-bottom:6px;">Remit Payment To</p>' +
    '<p class="pay" style="margin-bottom:2px;">' + REMIT_TO.name + '</p>' +
    '<p style="font-size:13px;color:#333;margin-bottom:2px;">' + REMIT_TO.street + '</p>' +
    '<p style="font-size:13px;color:#333;margin-bottom:12px;">' + REMIT_TO.cityStateZip + '</p>' +
    '<p>Questions? Contact wholesaler@4skin.biz</p><p>Thank you for your business!</p></div>' +
    '</body></html>';

  const invoiceWindow = window.open('', '_blank');
  if (!invoiceWindow) { alert('Please allow popups to view the invoice PDF.'); return; }
  invoiceWindow.document.write(html);
  invoiceWindow.document.close();
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export function InvoiceManagement({ orders, customers, reps, products, loading }) {
  const [view, setView] = useState('list');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [emailSending, setEmailSending] = useState(null);

  // Real-time invoices listener
  useEffect(() => {
    const q = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setInvoicesLoading(false);
    }, (err) => {
      console.error('Invoice listen error:', err);
      setInvoicesLoading(false);
    });
    return () => unsub();
  }, []);

  // Computed stats
  const stats = useMemo(() => {
    const now = new Date();
    const outstanding = invoices.filter(i => ['sent', 'viewed', 'partial'].includes(i.status));
    const overdue = invoices.filter(i => {
      if (['paid', 'void', 'draft'].includes(i.status)) return false;
      const due = i.dueDate?.toDate?.() || new Date(i.dueDate);
      return now > due;
    });
    const paid = invoices.filter(i => i.status === 'paid');
    const paidThisMonth = paid.filter(i => {
      const d = i.paidAt?.toDate?.() || (i.paidAt ? new Date(i.paidAt) : null);
      return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    return {
      draftCount: invoices.filter(i => i.status === 'draft').length,
      outstandingCount: outstanding.length,
      outstandingTotal: outstanding.reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0),
      overdueCount: overdue.length,
      overdueTotal: overdue.reduce((s, i) => s + ((i.total || 0) - (i.amountPaid || 0)), 0),
      paidCount: paid.length,
      paidTotal: paid.reduce((s, i) => s + (i.total || 0), 0),
      paidThisMonth: paidThisMonth.reduce((s, i) => s + (i.total || 0), 0),
    };
  }, [invoices]);

  // Filter
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const matchesSearch = !searchTerm ||
        inv.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase());
      let matchesStatus = statusFilter === 'all';
      if (statusFilter === 'outstanding') matchesStatus = ['sent', 'viewed', 'partial'].includes(inv.status);
      else if (statusFilter === 'overdue') {
        const due = inv.dueDate?.toDate?.() || new Date(inv.dueDate);
        matchesStatus = !['paid', 'void', 'draft'].includes(inv.status) && new Date() > due;
      } else if (statusFilter !== 'all') matchesStatus = inv.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, statusFilter]);

  // Actions
  const handleStatusChange = async (invoiceId, newStatus, extra = {}) => {
    setActionLoading(invoiceId);
    try {
      const update = { status: newStatus, updatedAt: Timestamp.now(), ...extra };
      if (newStatus === 'paid') {
        update.paidAt = Timestamp.now();
        const inv = invoices.find(i => i.id === invoiceId);
        if (inv && !extra.amountPaid) update.amountPaid = inv.total;
      }
      await updateDoc(doc(db, 'invoices', invoiceId), update);
    } catch (err) {
      console.error('Status update error:', err);
      alert('Failed to update invoice status');
    } finally { setActionLoading(null); }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Delete this invoice? This cannot be undone.')) return;
    setActionLoading(invoiceId);
    try { await deleteDoc(doc(db, 'invoices', invoiceId)); }
    catch (err) { console.error('Delete error:', err); alert('Failed to delete invoice'); }
    finally { setActionLoading(null); }
  };

  const handleSendEmail = async (invoice) => {
    setEmailSending(invoice.id);
    try {
      const sendInvoiceEmail = httpsCallable(functions, 'sendInvoiceEmail');
      await sendInvoiceEmail({ invoiceId: invoice.id });
      if (invoice.status === 'draft') {
        await updateDoc(doc(db, 'invoices', invoice.id), {
          status: 'sent', sentAt: Timestamp.now(), updatedAt: Timestamp.now()
        });
      }
      alert('Invoice emailed successfully!');
    } catch (err) {
      console.error('Email error:', err);
      alert('Failed to send email. Make sure the sendInvoiceEmail Cloud Function is deployed.\n\nError: ' + err.message);
    } finally { setEmailSending(null); }
  };

  const handleDuplicate = (invoice) => {
    const dup = { ...invoice, invoiceNumber: generateInvoiceNumber(), status: 'draft', amountPaid: 0, paidAt: null, sentAt: null };
    delete dup.id;
    setEditingInvoice(dup);
    setView('create');
  };

  if (loading || invoicesLoading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto" size={32} />
        <p className="text-gray-500 mt-4">Loading invoices...</p>
      </div>
    );
  }

  // Create / Edit form
  if (view === 'create' || view === 'edit') {
    return (
      <InvoiceForm
        customers={customers} reps={reps} products={products} orders={orders}
        initial={editingInvoice} isEdit={view === 'edit'}
        onSave={() => { setView('list'); setEditingInvoice(null); }}
        onCancel={() => { setView('list'); setEditingInvoice(null); }}
      />
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Invoices</h2>
        <button onClick={() => { setEditingInvoice(null); setView('create'); }}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
          <Plus size={18} /> New Invoice
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { icon: FileText, label: 'Drafts', value: stats.draftCount, bg: 'bg-gray-50', fg: 'text-gray-600' },
          { icon: Send, label: 'Outstanding', value: stats.outstandingCount, sub: '$' + stats.outstandingTotal.toFixed(2), bg: 'bg-blue-50', fg: 'text-blue-600' },
          { icon: AlertTriangle, label: 'Overdue', value: stats.overdueCount, sub: '$' + stats.overdueTotal.toFixed(2), bg: 'bg-red-50', fg: 'text-red-600' },
          { icon: Check, label: 'Paid', value: stats.paidCount, sub: '$' + stats.paidTotal.toFixed(2), bg: 'bg-green-50', fg: 'text-green-600' },
          { icon: DollarSign, label: 'This Month', value: '$' + stats.paidThisMonth.toFixed(2), bg: 'bg-emerald-50', fg: 'text-emerald-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className={'w-9 h-9 rounded-full flex items-center justify-center ' + s.bg + ' ' + s.fg}>
                <s.icon size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500 truncate">{s.label}</p>
                <p className="text-lg font-bold truncate">{s.value}</p>
                {s.sub && <p className="text-xs text-gray-500 truncate">{s.sub}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search invoices, customers..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:border-transparent" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border rounded-lg">
            <option value="all">All Statuses</option>
            <option value="draft">Drafts</option>
            <option value="outstanding">Outstanding</option>
            <option value="overdue">Overdue</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial Payment</option>
            <option value="paid">Paid</option>
            <option value="void">Voided</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {filteredInvoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FileText size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No invoices found</p>
          <p className="text-gray-400 text-sm mt-1">Create your first invoice to get started</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Due</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredInvoices.map(inv => {
                const invDate = inv.invoiceDate?.toDate?.() || new Date(inv.invoiceDate);
                const dueDate = inv.dueDate?.toDate?.() || new Date(inv.dueDate);
                const isOverdue = new Date() > dueDate && !['paid', 'void', 'draft'].includes(inv.status);
                const displayStatus = isOverdue ? 'overdue' : inv.status;
                const statusInfo = STATUSES[displayStatus] || STATUSES.draft;
                const balance = (inv.total || 0) - (inv.amountPaid || 0);
                const isExpanded = expandedRow === inv.id;
                const rep = inv.repId ? (reps || []).find(r => r.id === inv.repId) : null;
                const isLoading = actionLoading === inv.id;
                const isSending = emailSending === inv.id;

                return (
                  <InvoiceTableRow key={inv.id}
                    inv={inv} invDate={invDate} dueDate={dueDate} isOverdue={isOverdue}
                    displayStatus={displayStatus} statusInfo={statusInfo} balance={balance}
                    isExpanded={isExpanded} rep={rep} isLoading={isLoading} isSending={isSending}
                    onToggleExpand={() => setExpandedRow(isExpanded ? null : inv.id)}
                    onEdit={() => { setEditingInvoice(inv); setView('edit'); }}
                    onDelete={() => handleDelete(inv.id)}
                    onStatusChange={handleStatusChange}
                    onSendEmail={() => handleSendEmail(inv)}
                    onDuplicate={() => handleDuplicate(inv)}
                    onViewPDF={() => openInvoicePDF(inv)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Legacy order invoices */}
      <LegacyInvoiceOrders orders={orders} customers={customers} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INVOICE TABLE ROW
// ═══════════════════════════════════════════════════════════════════════════════
function InvoiceTableRow({
  inv, invDate, dueDate, isOverdue, displayStatus, statusInfo, balance,
  isExpanded, rep, isLoading, isSending,
  onToggleExpand, onEdit, onDelete, onStatusChange, onSendEmail, onDuplicate, onViewPDF
}) {
  return (
    <>
      <tr className={'cursor-pointer hover:bg-gray-50 transition-colors' + (inv.status === 'void' ? ' opacity-50' : '')}
        onClick={onToggleExpand}>
        <td className="px-4 py-3">
          <p className="font-mono font-medium text-sm">{inv.invoiceNumber}</p>
          {inv.orderId && <p className="text-xs text-gray-400">Order #{inv.orderId.slice(-8).toUpperCase()}</p>}
        </td>
        <td className="px-4 py-3">
          <p className="font-medium text-sm truncate max-w-[200px]">{inv.customerName}</p>
          <p className="text-xs text-gray-500 truncate max-w-[200px]">{inv.customerEmail}</p>
          {rep && <p className="text-xs text-blue-500">Rep: {rep.name || rep.contactName}</p>}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">{invDate.toLocaleDateString()}</td>
        <td className={'px-4 py-3 text-sm hidden md:table-cell ' + (isOverdue ? 'text-red-600 font-medium' : 'text-gray-600')}>
          {dueDate.toLocaleDateString()}
        </td>
        <td className="px-4 py-3 text-right">
          <p className="font-medium">${(inv.total || 0).toFixed(2)}</p>
          {(inv.amountPaid || 0) > 0 && inv.status !== 'paid' && (
            <p className="text-xs text-green-600">Paid: ${inv.amountPaid.toFixed(2)}</p>
          )}
        </td>
        <td className="px-4 py-3 text-center">
          <span className={'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ' + statusInfo.color}>
            {statusInfo.label}
          </span>
        </td>
        <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-center gap-1">
            <button onClick={onViewPDF} className="p-1.5 hover:bg-gray-100 rounded" title="View PDF"><Printer size={15} /></button>
            {inv.customerEmail && (
              <button onClick={onSendEmail} disabled={isSending}
                className="p-1.5 hover:bg-blue-50 text-blue-600 rounded" title="Email Invoice">
                {isSending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
              </button>
            )}
            {inv.status !== 'paid' && inv.status !== 'void' && (
              <button onClick={() => onStatusChange(inv.id, 'paid')} disabled={isLoading}
                className="p-1.5 hover:bg-green-50 text-green-600 rounded" title="Mark Paid">
                {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              </button>
            )}
            <button onClick={onToggleExpand} className="p-1.5 hover:bg-gray-100 rounded">
              {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr><td colSpan="7" className="bg-gray-50 px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Line Items</h4>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500"><th className="text-left pb-1">Item</th><th className="text-right pb-1">Qty</th><th className="text-right pb-1">Price</th><th className="text-right pb-1">Total</th></tr></thead>
                <tbody>
                  {(inv.lineItems || []).map((item, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td className="py-1">{item.description}</td>
                      <td className="text-right py-1">{item.quantity}</td>
                      <td className="text-right py-1">${Number(item.unitPrice).toFixed(2)}</td>
                      <td className="text-right py-1">${(item.quantity * item.unitPrice).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {inv.notes && <div className="mt-3 p-2 bg-yellow-50 rounded text-xs text-gray-600"><strong>Notes:</strong> {inv.notes}</div>}
            </div>
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Actions</h4>
              <div className="flex flex-col gap-2">
                {inv.status === 'draft' && (
                  <button onClick={() => onStatusChange(inv.id, 'sent')} className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                    <Send size={14} /> Mark as Sent
                  </button>
                )}
                {['sent', 'viewed', 'partial'].includes(inv.status) && (
                  <button onClick={() => {
                    const amt = prompt('Enter partial payment amount (leave blank for full):');
                    if (amt === null) return;
                    if (amt === '' || isNaN(parseFloat(amt))) { onStatusChange(inv.id, 'paid'); }
                    else {
                      const newPaid = (inv.amountPaid || 0) + parseFloat(amt);
                      if (newPaid >= inv.total) onStatusChange(inv.id, 'paid', { amountPaid: inv.total });
                      else onStatusChange(inv.id, 'partial', { amountPaid: newPaid });
                    }
                  }} className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700">
                    <DollarSign size={14} /> Record Payment
                  </button>
                )}
                {inv.status === 'partial' && (
                  <button onClick={() => onStatusChange(inv.id, 'paid', { amountPaid: inv.total })}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700">
                    <Check size={14} /> Mark Fully Paid
                  </button>
                )}
                <button onClick={onEdit} className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200">
                  <Edit2 size={14} /> Edit Invoice
                </button>
                <button onClick={onDuplicate} className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200">
                  <Copy size={14} /> Duplicate
                </button>
                {inv.status !== 'void' && inv.status !== 'paid' && (
                  <button onClick={() => onStatusChange(inv.id, 'void')} className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-red-600 text-sm rounded hover:bg-red-50">
                    <X size={14} /> Void Invoice
                  </button>
                )}
                {inv.status === 'draft' && (
                  <button onClick={onDelete} className="flex items-center gap-2 px-3 py-2 text-red-500 text-sm rounded hover:bg-red-50">
                    <Trash2 size={14} /> Delete
                  </button>
                )}
              </div>
              <div className="mt-4">
                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Timeline</h4>
                <div className="text-xs text-gray-500 space-y-1">
                  {inv.createdAt && <p>Created: {(inv.createdAt?.toDate?.() || new Date(inv.createdAt)).toLocaleString()}</p>}
                  {inv.sentAt && <p>Sent: {(inv.sentAt?.toDate?.() || new Date(inv.sentAt)).toLocaleString()}</p>}
                  {inv.paidAt && <p>Paid: {(inv.paidAt?.toDate?.() || new Date(inv.paidAt)).toLocaleString()}</p>}
                </div>
              </div>
            </div>
          </div>
        </td></tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INVOICE FORM (CREATE / EDIT)
// ═══════════════════════════════════════════════════════════════════════════════
function InvoiceForm({ customers, reps, products, orders, initial, isEdit, onSave, onCancel }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showOrderPicker, setShowOrderPicker] = useState(false);

  const today = new Date();
  const defaultDue = new Date(today);
  defaultDue.setDate(defaultDue.getDate() + 30);

  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        customerId: initial.customerId || '',
        customerName: initial.customerName || '',
        customerContact: initial.customerContact || '',
        customerEmail: initial.customerEmail || '',
        customerAddress: initial.customerAddress || '',
        repId: initial.repId || '',
        invoiceNumber: initial.invoiceNumber || generateInvoiceNumber(),
        invoiceDate: initial.invoiceDate ? formatDateInput(initial.invoiceDate?.toDate?.() || new Date(initial.invoiceDate)) : formatDateInput(today),
        dueDate: initial.dueDate ? formatDateInput(initial.dueDate?.toDate?.() || new Date(initial.dueDate)) : formatDateInput(defaultDue),
        terms: initial.terms || 'Net 30',
        lineItems: initial.lineItems?.length ? initial.lineItems.map(li => ({
          productId: li.productId || '', description: li.description || '', quantity: li.quantity || 1,
          unitPrice: li.unitPrice || 0, basePrice: li.basePrice || li.unitPrice || 0, sku: li.sku || '',
        })) : [{ productId: '', description: '', quantity: 1, unitPrice: 0, basePrice: 0, sku: '' }],
        notes: initial.notes || '',
        discount: initial.discount || 0,
        discountLabel: initial.discountLabel || '',
        tax: initial.tax || 0,
        shipping: initial.shipping || 0,
        shippingAddress: initial.shippingAddress || null,
        orderId: initial.orderId || '',
        status: initial.status || 'draft',
      };
    }
    return {
      customerId: '', customerName: '', customerContact: '', customerEmail: '', customerAddress: '', repId: '',
      invoiceNumber: generateInvoiceNumber(),
      invoiceDate: formatDateInput(today),
      dueDate: formatDateInput(defaultDue),
      terms: 'Net 30',
      lineItems: [{ productId: '', description: '', quantity: 1, unitPrice: 0, basePrice: 0, sku: '' }],
      notes: '', discount: 0, discountLabel: '', tax: 0, shipping: 0,
      shippingAddress: null, orderId: '', status: 'draft',
    };
  });

  const handleSelectCustomer = (customerId) => {
    const cust = customers.find(c => c.id === customerId);
    if (!cust) { setForm(f => ({ ...f, customerId: '', customerName: '', customerContact: '', customerEmail: '', customerAddress: '', repId: '' })); return; }
    const addr = cust.shippingLocations?.find(l => l.isDefault) || cust.shippingLocations?.[0] || null;
    setForm(f => ({
      ...f, customerId, customerName: cust.companyName || '', customerContact: cust.contactName || '',
      customerEmail: cust.email || '',
      customerAddress: addr ? (addr.street + ', ' + addr.city + ', ' + addr.state + ' ' + addr.zip) : '',
      repId: cust.repId || '', shippingAddress: addr || null,
    }));
  };

  const handleImportOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    const cust = customers.find(c => c.id === order.customerId);
    setForm(f => ({
      ...f, orderId, customerId: order.customerId || '',
      customerName: order.customerName || cust?.companyName || '',
      customerContact: cust?.contactName || '', customerEmail: order.customerEmail || cust?.email || '',
      repId: order.repId || cust?.repId || '', shippingAddress: order.shippingLocation || null,
      lineItems: (order.items || []).map(item => ({
        description: item.name, quantity: item.quantity, unitPrice: item.wholesalePrice, sku: item.id || '',
      })),
    }));
    setShowOrderPicker(false);
  };

  // Get the selected customer's tier discount
  const selectedCustomer = customers.find(c => c.id === form.customerId);
  const tierDiscount = selectedCustomer?.tierDiscount || selectedCustomer?.customDiscount || 0;

  const getDiscountedPrice = (basePrice) => {
    if (!tierDiscount) return basePrice;
    return Math.round(basePrice * (1 - tierDiscount / 100) * 100) / 100;
  };

  const handleAddProducts = (selectedProducts) => {
    const newItems = selectedProducts.map(p => ({
      productId: p.id,
      description: p.name,
      quantity: p.minQuantity || 1,
      unitPrice: getDiscountedPrice(p.wholesalePrice),
      basePrice: p.wholesalePrice,
      sku: p.sku || p.id,
    }));
    setForm(f => ({ ...f, lineItems: [...f.lineItems.filter(li => li.description), ...newItems] }));
    setShowProductPicker(false);
  };

  // When a product is selected from the dropdown on a line item row
  const handleLineItemProductChange = (index, productId) => {
    if (productId === 'custom') {
      updateLineItem(index, 'productId', '');
      updateLineItem(index, 'description', '');
      updateLineItem(index, 'unitPrice', 0);
      updateLineItem(index, 'basePrice', 0);
      updateLineItem(index, 'sku', '');
      return;
    }
    const product = (products || []).find(p => p.id === productId);
    if (!product) return;
    setForm(f => {
      const items = [...f.lineItems];
      items[index] = {
        ...items[index],
        productId: product.id,
        description: product.name,
        unitPrice: getDiscountedPrice(product.wholesalePrice),
        basePrice: product.wholesalePrice,
        sku: product.sku || product.id,
      };
      return { ...f, lineItems: items };
    });
  };

  const updateLineItem = (index, field, value) => {
    setForm(f => {
      const items = [...f.lineItems];
      items[index] = { ...items[index], [field]: value };
      return { ...f, lineItems: items };
    });
  };

  const addLineItem = () => setForm(f => ({ ...f, lineItems: [...f.lineItems, { productId: '', description: '', quantity: 1, unitPrice: 0, basePrice: 0, sku: '' }] }));
  const removeLineItem = (index) => setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, i) => i !== index) }));

  const subtotal = form.lineItems.reduce((s, li) => s + ((Number(li.quantity) || 0) * (Number(li.unitPrice) || 0)), 0);
  const total = subtotal - (Number(form.discount) || 0) + (Number(form.tax) || 0) + (Number(form.shipping) || 0);

  const handleTermsChange = (terms) => {
    const invDate = new Date(form.invoiceDate);
    let days = 30;
    if (terms === 'Net 15') days = 15;
    else if (terms === 'Net 45') days = 45;
    else if (terms === 'Net 60') days = 60;
    else if (terms === 'Due on Receipt') days = 0;
    const due = new Date(invDate);
    due.setDate(due.getDate() + days);
    setForm(f => ({ ...f, terms, dueDate: formatDateInput(due) }));
  };

  const handleSave = async (asDraft = true) => {
    if (!form.customerName) { setError('Customer name is required'); return; }
    if (form.lineItems.filter(li => li.description).length === 0) { setError('At least one line item is required'); return; }
    setSaving(true); setError('');
    try {
      const invoiceData = {
        customerId: form.customerId, customerName: form.customerName, customerContact: form.customerContact,
        customerEmail: form.customerEmail, customerAddress: form.customerAddress, repId: form.repId,
        invoiceNumber: form.invoiceNumber,
        invoiceDate: Timestamp.fromDate(new Date(form.invoiceDate)),
        dueDate: Timestamp.fromDate(new Date(form.dueDate)),
        terms: form.terms,
        lineItems: form.lineItems.filter(li => li.description).map(li => ({
          productId: li.productId || '', description: li.description, quantity: Number(li.quantity) || 0,
          unitPrice: Number(li.unitPrice) || 0, basePrice: Number(li.basePrice) || Number(li.unitPrice) || 0, sku: li.sku || '',
        })),
        subtotal: Math.round(subtotal * 100) / 100,
        discount: Number(form.discount) || 0, discountLabel: form.discountLabel,
        tax: Number(form.tax) || 0, shipping: Number(form.shipping) || 0,
        total: Math.round(total * 100) / 100,
        notes: form.notes, shippingAddress: form.shippingAddress,
        orderId: form.orderId || null,
        status: asDraft ? 'draft' : 'sent',
        amountPaid: isEdit ? (initial?.amountPaid || 0) : 0,
        updatedAt: Timestamp.now(),
      };
      if (isEdit && initial?.id) {
        await updateDoc(doc(db, 'invoices', initial.id), invoiceData);
      } else {
        invoiceData.createdAt = Timestamp.now();
        if (!asDraft) invoiceData.sentAt = Timestamp.now();
        await addDoc(collection(db, 'invoices'), invoiceData);
      }
      onSave();
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save invoice: ' + err.message);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">{isEdit ? 'Edit Invoice' : 'Create Invoice'}</h2>
        <button onClick={onCancel} className="flex items-center gap-2 text-gray-600 hover:text-gray-800"><X size={18} /> Cancel</button>
      </div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Customer & Line Items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer */}
          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2"><Building2 size={18} /> Customer</h3>
              {!isEdit && <button onClick={() => setShowOrderPicker(!showOrderPicker)} className="text-sm text-blue-600 hover:underline flex items-center gap-1"><FileText size={14} /> Import from Order</button>}
            </div>
            {showOrderPicker && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg max-h-60 overflow-y-auto">
                <p className="text-xs text-blue-700 font-medium mb-2">Select an order to import:</p>
                {(orders || []).filter(o => o.status !== 'Cancelled').slice(0, 20).map(o => {
                  const cust = customers.find(c => c.id === o.customerId);
                  return (
                    <button key={o.id} onClick={() => handleImportOrder(o.id)}
                      className="w-full text-left p-2 hover:bg-blue-100 rounded text-sm flex justify-between">
                      <span>{cust?.companyName || o.customerName || 'Unknown'} — #{o.id.slice(-8).toUpperCase()}</span>
                      <span className="text-gray-500">${(o.total || o.subtotal || 0).toFixed(2)}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Select Customer</label>
                <select value={form.customerId} onChange={(e) => handleSelectCustomer(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  <option value="">— Select or type manually —</option>
                  {(customers || []).map(c => <option key={c.id} value={c.id}>{c.companyName} {c.repId ? '(Rep)' : '(Direct)'}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Company Name *</label>
                <input type="text" value={form.customerName} onChange={(e) => setForm(f => ({ ...f, customerName: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Company name" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contact Name</label>
                <input type="text" value={form.customerContact} onChange={(e) => setForm(f => ({ ...f, customerContact: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input type="email" value={form.customerEmail} onChange={(e) => setForm(f => ({ ...f, customerEmail: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Line Items</h3>
              <button onClick={() => setShowProductPicker(!showProductPicker)} className="text-sm text-blue-600 hover:underline flex items-center gap-1"><Plus size={14} /> Quick Add Multiple</button>
            </div>
            {tierDiscount > 0 && (
              <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-700">
                Customer tier discount: <strong>{tierDiscount}% off</strong> wholesale prices
              </div>
            )}
            {showProductPicker && <ProductPicker products={products} onAdd={handleAddProducts} onClose={() => setShowProductPicker(false)} tierDiscount={tierDiscount} />}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-500 uppercase tracking-wider">
                  <th className="text-left pb-2 w-[40%]">Product / Description</th>
                  <th className="text-right pb-2 w-[12%]">Qty</th>
                  <th className="text-right pb-2 w-[18%]">Unit Price</th>
                  <th className="text-right pb-2 w-[15%]">Total</th>
                  <th className="pb-2 w-[7%]"></th>
                </tr></thead>
                <tbody>
                  {form.lineItems.map((item, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-2 pr-2">
                        <select
                          value={item.productId || 'custom'}
                          onChange={(e) => handleLineItemProductChange(i, e.target.value)}
                          className="w-full px-2 py-1.5 border rounded text-sm mb-1"
                        >
                          <option value="custom">— Custom Item —</option>
                          {(products || []).filter(p => p.active !== false).map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} — ${p.wholesalePrice?.toFixed(2)}{tierDiscount > 0 ? ' → $' + getDiscountedPrice(p.wholesalePrice).toFixed(2) : ''}{p.sku ? ' (SKU: ' + p.sku + ')' : ''}
                            </option>
                          ))}
                        </select>
                        {!item.productId && (
                          <input type="text" value={item.description} onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                            className="w-full px-2 py-1.5 border rounded text-sm" placeholder="Custom item description" />
                        )}
                        {item.productId && item.basePrice && tierDiscount > 0 && (
                          <p className="text-xs text-gray-400 mt-0.5 px-1">
                            Base: ${Number(item.basePrice).toFixed(2)} → {tierDiscount}% off
                          </p>
                        )}
                      </td>
                      <td className="py-2 px-1"><input type="number" value={item.quantity} onChange={(e) => updateLineItem(i, 'quantity', parseInt(e.target.value) || 0)} className="w-full px-2 py-1.5 border rounded text-sm text-right" min="1" /></td>
                      <td className="py-2 px-1">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                          <input type="number" value={item.unitPrice} onChange={(e) => updateLineItem(i, 'unitPrice', parseFloat(e.target.value) || 0)} className="w-full pl-6 pr-2 py-1.5 border rounded text-sm text-right" step="0.01" min="0" />
                        </div>
                      </td>
                      <td className="py-2 px-1 text-right font-medium">${((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)).toFixed(2)}</td>
                      <td className="py-2 pl-1">{form.lineItems.length > 1 && <button onClick={() => removeLineItem(i)} className="p-1 hover:bg-red-50 text-red-400 rounded"><Trash2 size={14} /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={addLineItem} className="mt-3 text-sm text-gray-600 hover:text-black flex items-center gap-1"><Plus size={14} /> Add Line Item</button>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="font-semibold mb-3">Notes</h3>
            <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} placeholder="Payment instructions, thank you message, etc." />
          </div>
        </div>

        {/* Right: Details & Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Hash size={18} /> Invoice Details</h3>
            <div className="space-y-3">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Invoice Number</label>
                <input type="text" value={form.invoiceNumber} onChange={(e) => setForm(f => ({ ...f, invoiceNumber: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Invoice Date</label>
                <input type="date" value={form.invoiceDate} onChange={(e) => setForm(f => ({ ...f, invoiceDate: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Payment Terms</label>
                <select value={form.terms} onChange={(e) => handleTermsChange(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  {TERMS_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
                <input type="date" value={form.dueDate} onChange={(e) => setForm(f => ({ ...f, dueDate: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="font-semibold mb-4">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span className="font-medium">${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between items-center"><span className="text-gray-600">Discount</span>
                <div className="flex items-center gap-1"><span className="text-gray-400">-$</span>
                  <input type="number" value={form.discount} onChange={(e) => setForm(f => ({ ...f, discount: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 border rounded text-right text-sm" step="0.01" min="0" /></div></div>
              <div className="flex justify-between items-center"><span className="text-gray-600">Tax</span>
                <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                  <input type="number" value={form.tax} onChange={(e) => setForm(f => ({ ...f, tax: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 border rounded text-right text-sm" step="0.01" min="0" /></div></div>
              <div className="flex justify-between items-center"><span className="text-gray-600">Shipping</span>
                <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                  <input type="number" value={form.shipping} onChange={(e) => setForm(f => ({ ...f, shipping: parseFloat(e.target.value) || 0 }))} className="w-20 px-2 py-1 border rounded text-right text-sm" step="0.01" min="0" /></div></div>
              <div className="border-t pt-2 flex justify-between text-lg font-bold"><span>Total</span><span>${total.toFixed(2)}</span></div>
            </div>
          </div>

          <div className="space-y-2">
            <button onClick={() => handleSave(true)} disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-black text-white px-4 py-3 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {saving ? <Loader2 size={18} className="animate-spin" /> : <FileText size={18} />}
              {isEdit ? 'Update Invoice' : 'Save as Draft'}
            </button>
            {!isEdit && (
              <button onClick={() => handleSave(false)} disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                Save & Mark as Sent
              </button>
            )}
            <button onClick={onCancel} className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT PICKER
// ═══════════════════════════════════════════════════════════════════════════════
function ProductPicker({ products, onAdd, onClose, tierDiscount = 0 }) {
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState('');

  const filtered = (products || []).filter(p =>
    p.active !== false && (!search || p.name?.toLowerCase().includes(search.toLowerCase()))
  );

  const getDiscounted = (price) => {
    if (!tierDiscount) return price;
    return Math.round(price * (1 - tierDiscount / 100) * 100) / 100;
  };

  const toggleProduct = (product) => {
    setSelected(s => {
      const next = { ...s };
      if (next[product.id]) delete next[product.id];
      else next[product.id] = product;
      return next;
    });
  };

  return (
    <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-gray-600">Select products to add:</p>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
      </div>
      <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className="w-full px-3 py-1.5 border rounded text-sm mb-2" />
      <div className="max-h-48 overflow-y-auto space-y-1">
        {filtered.map(p => (
          <label key={p.id} className="flex items-center gap-2 p-1.5 hover:bg-white rounded cursor-pointer text-sm">
            <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggleProduct(p)} className="rounded" />
            <span className="flex-1">
              {p.name}
              {p.sku && <span className="text-gray-400 ml-1 text-xs">({p.sku})</span>}
            </span>
            <span className="text-gray-500">
              {tierDiscount > 0 ? (
                <><span className="line-through text-gray-400">${p.wholesalePrice?.toFixed(2)}</span> <span className="text-green-600 font-medium">${getDiscounted(p.wholesalePrice).toFixed(2)}</span></>
              ) : (
                '$' + p.wholesalePrice?.toFixed(2)
              )}
            </span>
          </label>
        ))}
      </div>
      {Object.keys(selected).length > 0 && (
        <button onClick={() => onAdd(Object.values(selected))} className="mt-2 bg-black text-white px-3 py-1.5 rounded text-sm hover:bg-gray-800">
          Add {Object.keys(selected).length} Product{Object.keys(selected).length > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEGACY ORDER INVOICES
// ═══════════════════════════════════════════════════════════════════════════════
function LegacyInvoiceOrders({ orders, customers }) {
  const invoiceOrders = useMemo(() => {
    return (orders || []).filter(o => o.paymentMethod === 'customerInvoice')
      .sort((a, b) => {
        const da = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const db2 = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return db2 - da;
      });
  }, [orders]);

  if (invoiceOrders.length === 0) return null;

  return (
    <div className="mt-8">
      <h3 className="text-lg font-bold mb-3 flex items-center gap-2"><Clock size={18} /> Legacy Order Invoices</h3>
      <p className="text-sm text-gray-500 mb-4">Orders placed with "Customer Invoiced" payment method. Consider creating formal invoices for these.</p>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Order</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Customer</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Payment</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {invoiceOrders.slice(0, 10).map(order => {
              const cust = customers.find(c => c.id === order.customerId);
              return (
                <tr key={order.id}>
                  <td className="px-4 py-2 font-mono">#{order.id.slice(-8).toUpperCase()}</td>
                  <td className="px-4 py-2">{cust?.companyName || order.customerName || 'Unknown'}</td>
                  <td className="px-4 py-2 text-right">${(order.total || order.subtotal || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={'px-2 py-0.5 text-xs rounded-full ' + (order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')}>
                      {order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
